import { usePermission } from '@backstage/plugin-permission-react';
import {
  announcementCreatePermission,
  announcementDeletePermission,
  announcementReadPermission,
  announcementUpdatePermission,
} from '@internal/backstage-plugin-announcement-common';

/**
 * Resolves all announcement RBAC permissions in a single place so components
 * don't need to repeat the four `usePermission` calls and aggregate their
 * loading/error state by hand.
 *
 * All four checks always run (fixed hook order), so the result is safe to use
 * from any component regardless of which flags it actually consumes.
 */
export const useAnnouncementPermissions = (): {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  loading: boolean;
  error?: Error;
} => {
  const {
    allowed: canRead,
    loading: loadingRead,
    error: readError,
  } = usePermission({ permission: announcementReadPermission });
  const {
    allowed: canCreate,
    loading: loadingCreate,
    error: createError,
  } = usePermission({ permission: announcementCreatePermission });
  const {
    allowed: canUpdate,
    loading: loadingUpdate,
    error: updateError,
  } = usePermission({ permission: announcementUpdatePermission });
  const {
    allowed: canDelete,
    loading: loadingDelete,
    error: deleteError,
  } = usePermission({ permission: announcementDeletePermission });

  return {
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    loading: loadingRead || loadingCreate || loadingUpdate || loadingDelete,
    error: readError || createError || updateError || deleteError,
  };
};
